import * as React from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Message } from "./types";
import { ChatField } from "./ChatField";
import { ChatAiMessage } from "./ChatAiMessage";
import { ChatUserMessage } from "./ChatUserMessage";
import { TransientEventsDisplay } from "./TransientEventsDisplay";
import { DocumentsSidebar } from "./DocumentsSidebar";
import type { TransientEvent } from "../../types";
import type { ChunkDocuments } from "./types";
import { Edit as EditIcon } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export interface ChatSendOptions {
  short_answer?: boolean; // Esaustiva (false) o breve (true), default false
  use_pro_model?: boolean; // Veloce (false) o Performante (true), default false
}

export type FeedbackAction = "like" | "dislike" | "reset";

interface ChatProps {
  messages: Message[];
  isLoading?: boolean;
  transientEvents?: TransientEvent[];
  onSendMessage?: (message: string, options?: ChatSendOptions) => void;
  sessionId?: string;
  feedbackMap?: Record<string, 1 | -1>;
  onFeedback?: (
    chatId: string,
    messageId: string,
    action: FeedbackAction,
    comment?: string | null
  ) => void;
  onRetry?: (chatId: string, aiMessageId: string) => void;
  editingMessageId?: string;
  editingContent?: string;
  onEditingContentChange?: (value: string) => void;
  onStartEdit?: (messageId: string, content: string) => void;
  onSendFromEdit?: (content: string) => void;
  onCancelEdit?: () => void;
  shortAnswer?: boolean;
  useProModel?: boolean;
  onChangeShortAnswer?: (value: boolean) => void;
  onChangeUseProModel?: (value: boolean) => void;
}

export const Chat: React.FC<ChatProps> = ({
  messages,
  isLoading = false,
  transientEvents = [],
  onSendMessage,
  sessionId,
  feedbackMap = {},
  onFeedback,
  onRetry,
  editingMessageId,
  editingContent = "",
  onEditingContentChange,
  onStartEdit,
  onSendFromEdit,
  onCancelEdit,
  shortAnswer = false,
  useProModel = false,
  onChangeShortAnswer,
  onChangeUseProModel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [message, setMessage] = React.useState("");
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const [documentsSidebarOpen, setDocumentsSidebarOpen] = React.useState(false);
  const [selectedDocuments, setSelectedDocuments] = React.useState<
    ChunkDocuments | undefined
  >(undefined);

  const [feedbackModal, setFeedbackModal] = React.useState<{
    open: boolean;
    chatId: string | null;
    messageId: string | null;
    action: "like" | "dislike" | null;
  }>({ open: false, chatId: null, messageId: null, action: null });
  const [feedbackComment, setFeedbackComment] = React.useState("");

  const isEditing = Boolean(editingMessageId && onSendFromEdit);
  const fieldValue = isEditing ? editingContent : message;
  const setFieldValue = isEditing
    ? (onEditingContentChange ?? (() => {}))
    : setMessage;

  const handleSend = () => {
    const text = (isEditing ? editingContent : message).trim();
    if (!text || isLoading) return;
    if (isEditing) {
      onSendFromEdit?.(text);
    } else {
      onSendMessage?.(text, {
        short_answer: shortAnswer,
        use_pro_model: useProModel,
      });
      setMessage("");
    }
  };

  const handleOpenDocuments = (documents: ChunkDocuments) => {
    setSelectedDocuments(documents);
    setDocumentsSidebarOpen(true);
  };

  const handleCloseDocuments = () => {
    setDocumentsSidebarOpen(false);
    setSelectedDocuments(undefined);
  };

  const isEmpty = messages.length === 0;
  const sidebarWidth = 396;

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, transientEvents]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        bgcolor: "background.default",
        position: "relative",
      }}
    >
      {/* Documents Sidebar */}
      <DocumentsSidebar
        open={documentsSidebarOpen}
        onClose={handleCloseDocuments}
        documents={selectedDocuments}
      />

      {/* Main Chat Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          mr: documentsSidebarOpen ? `${sidebarWidth}px` : 0,
          transition: "margin-right 0.3s ease",
          position: "relative",
          overflow: "hidden",
          borderTopRightRadius: documentsSidebarOpen ? "2rem" : 0,
          borderBottomRightRadius: documentsSidebarOpen ? "2rem" : 0,
          bgcolor: "white",
        }}
      >
        {/* Top Bar */}
        <AppBar
          position="absolute"
          elevation={0}
          sx={{
            bgcolor: "#FFFFFF",
            top: 0,
            left: 0,
            width: "calc(100% - 16px)",
          }}
        >
          <Toolbar
            sx={{
              justifyContent: "space-between",
              pl: isMobile ? 8 : 4,
              pr: 0,
              py: 0,
              minHeight: "54px !important",
              background: "trasparent",
            }}
            disableGutters={true}
          >
            <Box
              sx={{
                display: "flex",
                gap: 0,
                alignItems: "center",
                fontSize: "0.5rem",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(80, 80, 80, 0.9)",
                  fontWeight: 400,
                }}
              >
                Modello
              </Typography>
              <FormControl size="small">
                <Select
                  value={useProModel ? "Performante" : "Veloce"}
                  onChange={(e) =>
                    onChangeUseProModel?.(e.target.value === "Performante")
                  }
                  IconComponent={() => null}
                  sx={{
                    bgcolor: "trasparent",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                  displayEmpty
                  renderValue={(v) => (
                    <Typography>
                      {v}
                      <KeyboardArrowDownIcon></KeyboardArrowDownIcon>
                    </Typography>
                  )}
                >
                  <MenuItem value="Performante">Performante</MenuItem>
                  <MenuItem value="Veloce">Veloce</MenuItem>
                </Select>
              </FormControl>

              <Typography
                variant="body1"
                sx={{
                  color: "rgba(80, 80, 80, 0.9)",
                  fontWeight: 400,
                }}
              >
                Risposta
              </Typography>
              <FormControl size="small">
                <Select
                  value={shortAnswer ? "Breve" : "Esaustiva"}
                  onChange={(e) =>
                    onChangeShortAnswer?.(e.target.value === "Breve")
                  }
                  IconComponent={() => null}
                  sx={{
                    bgcolor: "trasparent",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                  displayEmpty
                  renderValue={(v) => (
                    <Typography>
                      {v}
                      <KeyboardArrowDownIcon></KeyboardArrowDownIcon>
                    </Typography>
                  )}
                >
                  <MenuItem value="Esaustiva">Esaustiva</MenuItem>
                  <MenuItem value="Breve">Breve</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {
              // <IconButton sx={{ color: "#000000" }}>
              //   <SettingsIcon />
              // </IconButton>
            }
          </Toolbar>
        </AppBar>

        {/* Main Content Area */}
        {!isEmpty && (
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
              my: documentsSidebarOpen ? 5 : 0,
              pt: documentsSidebarOpen ? 0 : 5,
            }}
          >
            <Box
              sx={{
                px: 4,
                py: 2,
                width: "100%",
                maxWidth: "1000px",
                transition: "max-width 0.3s ease",
              }}
            >
              {messages.map((msg, idx) =>
                msg.role === "user" ? (
                  <ChatUserMessage
                    key={msg.id}
                    message={msg}
                    onEdit={
                      sessionId &&
                      onStartEdit &&
                      !msg.disableEdit &&
                      !(
                        messages[idx + 1]?.role === "assistant" &&
                        messages[idx + 1]?.isStreaming
                      )
                        ? () => onStartEdit(msg.id, msg.content)
                        : undefined
                    }
                  />
                ) : (
                  <ChatAiMessage
                    key={msg.id}
                    message={msg}
                    onOpenDocuments={handleOpenDocuments}
                    feedbackValue={sessionId ? feedbackMap[msg.id] : undefined}
                    onLike={
                      sessionId && onFeedback
                        ? () =>
                            setFeedbackModal({
                              open: true,
                              chatId: sessionId,
                              messageId: msg.id,
                              action: "like",
                            })
                        : undefined
                    }
                    onDislike={
                      sessionId && onFeedback
                        ? () =>
                            setFeedbackModal({
                              open: true,
                              chatId: sessionId,
                              messageId: msg.id,
                              action: "dislike",
                            })
                        : undefined
                    }
                    onResetLike={
                      sessionId && onFeedback
                        ? () => onFeedback(sessionId, msg.id, "reset")
                        : undefined
                    }
                    onRetry={
                      sessionId && onRetry
                        ? () => onRetry(sessionId, msg.id)
                        : undefined
                    }
                    retryDisabled={isLoading}
                  />
                )
              )}

              {/* Transient Events Display */}
              {transientEvents.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",

                    gap: 0,
                  }}
                >
                  <Box sx={{ width: 39, flexShrink: 0 }} />
                  <Box
                    sx={{
                      flex: 1,
                      maxWidth: "90%",
                    }}
                  >
                    <TransientEventsDisplay events={transientEvents} />
                  </Box>
                </Box>
              )}

              <Box
                ref={messagesEndRef}
                sx={{ width: "1px", height: "200px" }}
              />
            </Box>
          </Box>
        )}

        {/* ChatField - Un solo componente che cambia posizione */}
        <Box
          sx={{
            position: "absolute",
            ...(isEmpty
              ? {
                  bottom: "50%",
                  transform: "translateY(50%)",
                }
              : {
                  bottom: 0,
                }),
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mx: 4,
            pb: 1,
            pt: isEmpty ? 4 : 0,
            backgroundColor: "white",
          }}
        >
          {isEmpty && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                mb: 4,
              }}
            >
              <Box
                component="img"
                src="/assets/round_light.png"
                alt="GreenEye"
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  flexShrink: 0,
                  objectFit: "cover",
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  color: "#424242",
                  fontWeight: 400,
                  fontSize: "1.5rem",
                }}
              >
                GreenEye
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {isEditing && (
              <Tooltip title="Stai modificando il messaggio">
                <EditIcon
                  sx={{
                    position: "absolute",
                    top: -36,
                    right: "calc(50% - 410px)",
                    color: "primary.main",
                    fontSize: 16,
                    zIndex: 1,
                  }}
                />
              </Tooltip>
            )}
            {isEditing && onCancelEdit && (
              <Typography
                component="button"
                type="button"
                onClick={onCancelEdit}
                sx={{
                  position: "absolute",
                  top: -36,
                  right: "calc(50% - 453px)",
                  ml: 4,
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  textDecoration: "underline",
                }}
              >
                Annulla
              </Typography>
            )}
            <ChatField
              message={fieldValue}
              onMessageChange={setFieldValue}
              onSend={handleSend}
              disabled={isLoading}
              isLoading={isLoading}
            />
          </Box>
        </Box>
      </Box>

      <Dialog
        open={feedbackModal.open}
        onClose={() => {
          setFeedbackModal((p) => ({ ...p, open: false }));
          setFeedbackComment("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {feedbackModal.action === "like"
            ? "Utile"
            : feedbackModal.action === "dislike"
              ? "Non utile"
              : "Feedback"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Commento"
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={feedbackComment}
            onChange={(e) => setFeedbackComment(e.target.value)}
            placeholder="Spiega brevemente perché hai dato questo feedback (opzionale). Il commento aiuta a migliorare le risposte."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFeedbackModal((p) => ({ ...p, open: false }));
              setFeedbackComment("");
            }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (
                feedbackModal.chatId &&
                feedbackModal.messageId &&
                feedbackModal.action &&
                onFeedback
              ) {
                onFeedback(
                  feedbackModal.chatId,
                  feedbackModal.messageId,
                  feedbackModal.action,
                  feedbackComment.trim() || null
                );
                setFeedbackModal({
                  open: false,
                  chatId: null,
                  messageId: null,
                  action: null,
                });
                setFeedbackComment("");
              }
            }}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
